import { createFileRoute, Outlet, redirect, useLocation, useNavigate } from "@tanstack/react-router"
import { TanStackRouterDevtoolsPanelInProd } from "@tanstack/react-router-devtools"
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools/production"
import { TanStackDevtools } from "@tanstack/react-devtools"
import { FeatureFlagOverridesPlugin } from "~/components/devtools/FeatureFlagOverridesPanel"
import { NpiNeighborsPlugin } from "~/components/devtools/NpiNeighborsPanel"
import { RenderProbesPlugin } from "~/components/devtools/RenderProbesPanel"
import { useRenderCount } from "~/hooks/useRenderCount"
import { setDevtoolsTriggerEl } from "~/utils/tanstack-devtools-trigger"
import { SidebarState, useSidebarState } from "~/hooks/useSidebarState"
import { useSessionStorage } from "usehooks-ts"
import { AnimatePresence, motion } from "motion/react"
import Sidebar from "~/components/reusable/Sidebar"
import HakiBottomBar from "~/components/sidebar/HakiBottomBar"
import { ShortcutsCheatsheet } from "~/components/sidebar/ShortcutsCheatsheet"
import { useLDClient, type LDContext } from "launchdarkly-react-client-sdk"
import { useUser } from "~/providers/userProvider"
import { useEffect, useRef, useState } from "react"
import { identifyUserForTracking } from "~/hooks/useTrackingService"
import { useAuth0 } from "@auth0/auth0-react"
import * as Sentry from "@sentry/react"
import { useCompany } from "~/hooks/useCompany"
import AuthError from "~/components/reusable/errors/AuthError"
import { useFlags, useHakioFlagsPending } from "~/features/feature-flags"
import { api } from "~/utils/api"
import { useSkuStore } from "~/providers/sku-store-provider"
import { useVariantTableDataHeavy } from "~/hooks/useVariantTableData"
import { useSkipSkuStoreData } from "~/hooks/useSkipSkuStoreData"
import LoadingOverlay, { WAITING_MESSAGES } from "~/components/reusable/LoadingOverlay"
import { useUpdatedForecasts } from "~/hooks/useForecasts"
import { toast } from "react-toastify"
import { useSyncOrgId } from "~/hooks/useOrgId"
import { isDevelopment, survicateWorkspaceKey } from "~/utils/environment"
import GenericError from "~/components/reusable/errors/GenericError"
import { NoAccessError } from "~/constants/errors"
import NotFound from "~/components/reusable/NotFound"
import { useIntercomSetup } from "~/hooks/useIntercomSetup"
import {
  CompleteProfileDialog,
  useNameCapture,
} from "~/components/settings/dialogs/CompleteProfileDialog"
import ConfigureExportDialog from "~/components/forecast/dialogs/ConfigureExportDialog"
import { useSafeLocalStorage } from "~/hooks/useSafeLocalStorage"
import { FilterScopeProvider } from "~/providers/FilterScopeProvider"
import { VariantProvider } from "~/context/VariantProvider"
import { customNamingSchema } from "~/utils/namingUtils"
import { setCompanyLocale } from "~/stores/number-format-store"
import { setFiscalConfig } from "~/stores/fiscal-config-store"
import { getFiscalConfig } from "~/utils/fiscalCalendar"
import Survicate from "@survicate/survicate-web-package/survicate_widget"
import { zodValidator } from "@tanstack/zod-adapter"
import { z } from "zod"

const AuthLayout = () => {
  useRenderCount("AuthLayout")
  const { user, isAuthenticated, error, isLoading: authLoading, loginWithRedirect } = useAuth0()
  const [isSurvicateReady, setIsSurvicateReady] = useState(false)
  const { data: organization } = api.auth0.getOrganization.useQuery(
    {
      organizationId: user?.org_id ?? "",
    },
    {
      enabled: !!user?.org_id,
    },
  )
  const { sidebarState } = useSidebarState()
  const { data: company } = useCompany()
  // Set while rendering, not in an effect: pages below read the store synchronously while they
  // render, and an effect lands only after the first render that has the company.
  if (company) setFiscalConfig(getFiscalConfig(company))
  const [showDebugBar, setShowDebugBar] = useSessionStorage("debugMode", isDevelopment())

  const ldClient = useLDClient()
  const navigate = useNavigate()
  const { role } = useUser()

  const { openIntercomChat } = useIntercomSetup({ user, organization, isAuthenticated })
  const location = useLocation()

  useEffect(() => {
    if (!company) return

    const lifecycleStage = company.lifecycleStage

    if (location.pathname === "/data-validation") return
    if (location.pathname.startsWith("/internal")) return

    const locked = ["not_customer"]
    if (!lifecycleStage || locked.includes(lifecycleStage)) {
      navigate({ to: "/data-validation" })
    }
  }, [company?.lifecycleStage, location.pathname, navigate])

  // Initialize Survicate
  useEffect(() => {
    Survicate.init({
      workspaceKey: survicateWorkspaceKey(),
    }).then(() => setIsSurvicateReady(true))
  }, [])

  useEffect(() => {
    if (!isSurvicateReady || !user) return

    Survicate.setVisitorTraits({
      user_id: user.sub!,
      email: user.email ?? "",
      name: user.name ?? "",
      org_id: user.org_id,
      org_name: user.org_name,
    })
  }, [isSurvicateReady, user])

  useEffect(() => {
    if (isAuthenticated && user !== undefined && role && ldClient) {
      const newContext: LDContext = {
        kind: "multi",
        user: {
          key: user.sub,
          name: user.name,
          email: user.email,
          department: user?.user_metadata?.department,
        },
        organization: {
          key: user.org_id,
          name: user.org_name,
        },
      }

      // An unreachable LaunchDarkly (blocked, offline) rejects here. The SDK already logs it, and
      // the anonymous context's flags stay in use, so it must not surface as an uncaught error.
      void ldClient.identify(newContext).catch(() => undefined)
      identifyUserForTracking({
        userId: user.email ?? undefined,
        orgId: user.org_id,
        orgName: user.org_name,
        auth0User: user,
        role,
      })
    }
  }, [isAuthenticated, ldClient, user, role])

  const syncProfile = api.user.syncProfile.useMutation()
  const syncedKey = useRef("")

  useEffect(() => {
    const key = `${user?.sub}:${user?.org_id}`
    if (user?.sub && syncedKey.current !== key) {
      syncedKey.current = key
      syncProfile.mutate()
    }
  }, [user?.sub, user?.org_id])

  useEffect(() => {
    if (user !== undefined) {
      Sentry.setUser({ id: user.sub, email: user.email })
      Sentry.setTag("org_id", user.org_name)
      Sentry.setExtra("company", user.org_name)
    }
  }, [user])

  useEffect(() => {
    if (company?.secondaryMoqGroupVariable)
      // We save this in the local storage since we need to access it in the formatSecondaryMOQName, which is
      // used deep in the internals of the datatable.
      // So in order to avoid allot of props drilling, we set in the local storage to be fetched when needed.
      sessionStorage.setItem("secondaryMOQ", company?.secondaryMoqGroupVariable)
  }, [company?.secondaryMoqGroupVariable])

  useEffect(() => {
    if (company) setCompanyLocale(company.numberFormatLocale ?? "de-DE")
  }, [company?.numberFormatLocale])

  const shouldFetchData = isAuthenticated && user !== undefined

  const { maintenanceMode: maintenanceModeFlag } = useFlags()
  const hakioFlagsPending = useHakioFlagsPending()
  const { permissions } = useUser()
  const selectedWarehouseId = useSkuStore((state) => state.warehouseId)
  const selectedChannel = useSkuStore((state) => state.channel)

  useSyncOrgId()

  const skipSkuStoreData = useSkipSkuStoreData()

  const { data: rawVariants, isLoading: variantsLoading } = useVariantTableDataHeavy(
    selectedWarehouseId,
    selectedChannel,
    shouldFetchData && !skipSkuStoreData,
  )
  const storeVariants = useSkuStore((state) => state.variants)
  const storeIsReady = !rawVariants?.length || storeVariants.length > 0

  const { data: customNaming, isLoading: customNamingLoading } =
    api.customNaming.getCustomNaming.useQuery()
  const [customNamingStorage, setCustomNamingStorage] = useSafeLocalStorage(
    "custom-naming",
    customNaming,
    customNamingSchema,
  )

  const { showNameCaptureDialog, markNameCaptured } = useNameCapture()

  useEffect(() => {
    setCustomNamingStorage(customNaming)
  }, [customNaming])

  useUpdatedForecasts(shouldFetchData && !skipSkuStoreData)
  const updateVariants = useSkuStore((state) => state.updateVariants)

  useEffect(() => {
    if (error?.name === "RefreshAccessTokenError") {
      void loginWithRedirect({
        appState: { returnTo: window.location.pathname },
      })
    }
  }, [error])

  useEffect(() => {
    if (rawVariants && rawVariants.length > 0) {
      updateVariants(rawVariants)
    }
  }, [rawVariants, selectedWarehouseId, updateVariants])
  const loadedVariants = rawVariants.length ? rawVariants : storeVariants

  useEffect(() => {
    if (maintenanceModeFlag === true) {
      openIntercomChat()
    }
  }, [maintenanceModeFlag])

  // Everything the whole app is gated on. `variantsLoading` is keyed on the sku-store's
  // warehouse/channel selection, so this drops back to false mid-session every time a planner
  // picks another warehouse.
  const appDataReady = !(
    !shouldFetchData ||
    authLoading ||
    !customNamingStorage ||
    !customNaming ||
    customNamingLoading ||
    (!skipSkuStoreData && (variantsLoading || !storeIsReady))
  )
  // Once the routed content has rendered, the overlay covers it rather than replacing it. Swapping
  // the Outlet out unmounted every page below it, so applying the warehouse filter silently threw
  // away unsaved edits, and being a conditional render rather than a navigation it did not even
  // trip the pages' own useBlocker prompts. The latch is only read while reloading; the first
  // ready render shows the Outlet through `appDataReady` itself.
  const [outletMounted, setOutletMounted] = useState(false)
  if (appDataReady && !outletMounted) setOutletMounted(true)

  if (maintenanceModeFlag && !permissions.isStaff) {
    return (
      <div className="flex h-screen w-full flex-col justify-center gap-4 bg-base text-center text-fg-base">
        <div className="text-4xl font-semibold">We will be right back!</div>
        <div className="text-lg font-medium">Hakio is currently down for maintenance</div>
      </div>
    )
  } else if (maintenanceModeFlag && permissions.isStaff) {
    // Show this warning for Hakio users
    toast.warning("Maintenance mode is on", {
      toastId: "maintenance-mode",
      position: "bottom-right",
      autoClose: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "colored",
    })
  }

  if (hakioFlagsPending) return <LoadingOverlay messages={WAITING_MESSAGES} height="h-screen" />

  return (
    <AnimatePresence initial={false}>
      <div key="app-layout" className="flex h-screen flex-col">
        <motion.div
          key="layout"
          className="grid min-h-0 flex-1 max-w-full grid-rows-[minmax(0,1fr)]"
          animate={{
            gridTemplateColumns: sidebarState === SidebarState.Expanded ? "221px 1fr" : "64px 1fr",
          }}
        >
          <Sidebar toggleDebugBar={() => setShowDebugBar(!showDebugBar)} />
          <div className="flex flex-col overflow-hidden relative" id="content-root">
            <div className="flex-1 overflow-y-auto bg-background">
              {(appDataReady || outletMounted) && (
                <FilterScopeProvider>
                  <VariantProvider variants={loadedVariants}>
                    <Outlet />
                  </VariantProvider>
                </FilterScopeProvider>
              )}
            </div>
            <HakiBottomBar />
            <ShortcutsCheatsheet />
            {!appDataReady && (
              <div className="absolute inset-0 z-40 bg-background">
                <LoadingOverlay size="lg" messages={WAITING_MESSAGES} height="h-full" />
              </div>
            )}
          </div>
        </motion.div>
        {showNameCaptureDialog && <CompleteProfileDialog onCompleted={markNameCaptured} />}
        <ConfigureExportDialog />
      </div>
      {permissions.isStaff && (
        <TanStackDevtools
          key="devtools"
          config={{
            openHotkey: ["CtrlOrMeta", "Shift", "A"],
            inspectHotkey: ["CtrlOrMeta", "Shift", "C"],
            customTrigger: (el) => {
              setDevtoolsTriggerEl(el)
              return <></>
            },
          }}
          plugins={[
            {
              name: "Tanstack Query",
              render: <ReactQueryDevtoolsPanel style={{ height: "100%" }} />,
              defaultOpen: true,
            },
            {
              name: "TanStack Router",
              render: <TanStackRouterDevtoolsPanelInProd />,
              defaultOpen: false,
            },
            FeatureFlagOverridesPlugin(),
            RenderProbesPlugin(),
            NpiNeighborsPlugin(),
          ]}
        />
      )}
    </AnimatePresence>
  )
}

/**
 * The active view, as `<savedViewId>` or `preset:<presetId>` (see `~/hooks/useViewRoute`). Declared
 * on `/_auth` — the one ancestor every authenticated page shares — because TanStack strips search
 * params the destination route's chain doesn't declare. The union matches what the router hands
 * over, since it parses a bare-digit param into a number, and values stay untransformed so they
 * round-trip unquoted. `.catch` keeps a malformed value from failing every authenticated route.
 */
const authSearchSchema = z.object({
  view: z.union([z.string(), z.number()]).optional().catch(undefined),
})

export const Route = createFileRoute("/_auth")({
  validateSearch: zodValidator(authSearchSchema),
  beforeLoad: async ({ location, context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({
        to: "/login",
        search: {
          redirect: location.href,
        },
      })
    }
  },
  errorComponent: ({ error }) => {
    if (
      error instanceof Error &&
      (error.message.includes("authentication") || error.cause === "auth_failed")
    ) {
      return <AuthError error={error} />
    } else if (error instanceof NoAccessError) {
      return <NotFound />
    }

    Sentry.captureException(error)
    return <GenericError error={error} message={error.message || "An unexpected error occurred"} />
  },
  component: AuthLayout,
})
